/**
 * The organization tree as the SPA uses it (Story 48, ADR-0033, task 208).
 *
 * `GET /orgs` returns the caller's visible subtree as a flat list, parents
 * before children. These helpers index it once and answer the questions
 * the switcher, the Organizations page and the Users page ask. An empty
 * list means the tree does not exist on this environment yet (before task
 * 207's migration): every caller then falls back to the flat Client view.
 */

import type { OrgNode, ServiceKey } from '@bluesphere/api-client';

export interface OrgIndex {
  /** The caller's home node, or null when there is no tree. */
  homeId: string | null;
  byId: Map<string, OrgNode>;
  children: Map<string, OrgNode[]>;
  /** Depth-first, parents first, children by name. */
  ordered: OrgNode[];
}

const byName = (a: OrgNode, b: OrgNode) =>
  (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' });

export function indexOrgs(homeId: string | null, nodes: OrgNode[]): OrgIndex {
  const byId = new Map(nodes.map((n) => [n.org_id, n]));
  const children = new Map<string, OrgNode[]>();
  for (const n of nodes) {
    if (n.parent_id && byId.has(n.parent_id)) {
      const list = children.get(n.parent_id) ?? [];
      list.push(n);
      children.set(n.parent_id, list);
    }
  }
  for (const list of children.values()) {
    // A parent's own Clients first, then its MSPs, each by name.
    list.sort((a, b) => Number(isParentKind(a)) - Number(isParentKind(b)) || byName(a, b));
  }
  const ordered: OrgNode[] = [];
  const top = nodes.filter((n) => !n.parent_id || !byId.has(n.parent_id));
  const walk = (n: OrgNode) => {
    ordered.push(n);
    for (const c of children.get(n.org_id) ?? []) walk(c);
  };
  top.sort(byName).forEach(walk);
  return { homeId, byId, children, ordered };
}

export const isParentKind = (n: OrgNode) => n.kind === 'simulint' || n.kind === 'msp';

/** Nodes from the top of the visible tree down to `id`, inclusive. */
export function pathTo(index: OrgIndex, id: string): OrgNode[] {
  const out: OrgNode[] = [];
  let n = index.byId.get(id);
  const seen = new Set<string>();
  while (n && !seen.has(n.org_id)) {
    seen.add(n.org_id);
    out.unshift(n);
    n = n.parent_id ? index.byId.get(n.parent_id) : undefined;
  }
  return out;
}

export function depthOf(index: OrgIndex, id: string): number {
  return Math.max(0, pathTo(index, id).length - 1);
}

/** `id` and everything beneath it. */
export function subtreeIds(index: OrgIndex, id: string): Set<string> {
  const out = new Set<string>();
  const stack = [id];
  while (stack.length) {
    const cur = stack.pop()!;
    if (out.has(cur)) continue;
    out.add(cur);
    for (const c of index.children.get(cur) ?? []) stack.push(c.org_id);
  }
  return out;
}

/** The Client ids under `id` (not the operations tenant). */
export function clientsUnder(index: OrgIndex, id: string): Set<string> {
  const out = new Set<string>();
  for (const nid of subtreeIds(index, id)) {
    if (index.byId.get(nid)?.kind === 'client') out.add(nid);
  }
  return out;
}

/**
 * Who set an inherited value, for "Set by": a node the caller cannot see
 * (above their home) is named as such rather than guessed.
 */
export function sourceLabel(index: OrgIndex, sourceId: string): string {
  const node = index.byId.get(sourceId);
  return node ? orgLabel(node) : 'the organization above yours';
}

/** A node's name as people read it: "Simulint", "Acme MSP", or the Client's name. */
export function orgLabel(node: OrgNode | undefined | null): string {
  return node?.name ?? 'Unnamed organization';
}

/** The services a node's parent resells: what may be offered beneath it. */
export function resellableAbove(index: OrgIndex, node: OrgNode): string[] {
  const parent = node.parent_id ? index.byId.get(node.parent_id) : undefined;
  return parent?.resellable ?? [];
}

/** The services an MSP can resell (Story 45's registry), as people name them. */
export const SERVICE_LABELS: Record<ServiceKey, string> = {
  mdr: 'MDR',
  latticeai_dlp: 'LatticeAI DLP',
};

/** The services, in a stable order for filters and lists. */
export const SERVICE_KEYS = Object.keys(SERVICE_LABELS) as ServiceKey[];

export function serviceLabel(key: string): string {
  return SERVICE_LABELS[key as ServiceKey] ?? key;
}
